import * as Engine from "./engine";
import _get from "lodash/get";

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// `{{a.b}}` interpolation over the render context, on top of the template pass.
//
// Two syntaxes because they run at different times: a template is evaluated
// with the section's own values, and this pass afterwards with the page context
// — so a section can emit `{{page.title}}` from inside a value somebody typed,
// which the template pass would have had no way to see.
//
// Bounded at three passes. A value containing `{{x}}` where x resolves to
// `{{x}}` is otherwise an infinite loop, and it is reachable by typing.
//
// `escape` IS THE POINT OF THE OPTION AND ITS DEFAULT IS WRONG FOR MARKUP.
//
// This pass substitutes raw by design, which is how an inline head block reads
// an attribute and still carries a tag. Applied to the BODY stream that same
// rawness is a hole: a section author who does the right thing and writes
// `<%= values.name %>` gets `<`, `>`, `"` and `'` escaped by the template pass
// and NOT `{`, so a value containing `{{site.values.evil}}` survives it intact
// and is spliced here as markup. `<%= %>` did not mean "escaped" while that was
// true. So the two body callers below pass `escape: true`, and the shell's
// three deliberately-raw fields (see `page.js`) do not.
//
// Escaping the body is free rather than a trade: every template stored on dev
// was read before this changed — 1290 of them — and **not one contains a
// `{{...}}` placeholder at all**. Nothing that exists renders differently.
export const interpolate = (payload, context, options = {}) => {
  const { escape = false, level = 0 } = options;

  if (!payload || typeof payload !== "string" || level > 3) {
    return payload;
  }

  const names = new Set();
  for (const match of payload.matchAll(/{{([a-zA-Z0-9-.'">[\]]*)}}/gm)) {
    names.add(match[1]);
  }

  if (names.size === 0) {
    return payload;
  }

  let res = payload;
  for (const name of names) {
    const value = _get(context, name);

    // `null` was reaching the page as the four characters "null" — the guard
    // was `!== undefined` and null is not undefined. Both are "no value".
    const text =
      value === undefined || value === null
        ? ""
        : escape
        ? Engine.escapeHtml(value)
        : String(value);

    // A FUNCTION replacement, not a string one. `String.replace` expands `$&`,
    // "$`", `$'` and `$1` inside a string replacement, so a site value that
    // happened to contain one was rewritten into something nobody typed —
    // silently, and only for values carrying a `$`.
    res = res.replace(new RegExp(`{{${escapeRegExp(name)}}}`, "g"), () => text);
  }

  return res.indexOf("{{") !== -1
    ? interpolate(res, context, { escape, level: level + 1 })
    : res;
};

// The rewrite the PREVIEW needs and a served page must never get.
//
// `data-edit="heading"` on an element of a template says "this text is the
// value called heading". The editor makes those nodes contentEditable and sends
// what you type back as a path into the page's own value object — so the path
// has to be absolute, and only the composer knows which element it is inside.
// A section written for one page therefore works unchanged on another.
//
// WHAT USED TO BE HERE, and why it is not: `document.location = ...` was
// rewritten to `document.locationEditor = ...`, on the grounds that an
// assignment navigating the frame away reads as the whole pane going blank with
// no way back. Gen-2 did it and the port copied it. It was a denylist of one
// string, and measured against the six ways to write the same line, five walked
// straight past it — `window.location =`, `location.href =`,
// `location.assign()`, `top.location =`, even `document . location =`. Worse,
// it only ever ran over the MARKUP field: a `code` element's Script field and a
// section's script — which is where a person actually writes a script — were
// never rewritten at all, so the one form it did name walked past too on the
// path most likely to carry it.
//
// A guard that stops one spelling out of six is not a guard, and this one cost
// something real: it made a tenant's script text in the editor differ from the
// script the served page runs, in a pane whose entire job is to show what the
// served page does. The navigation is caught where it can actually be caught —
// the parent watches the frame and brings it back. See the recovery in
// @repoflow/components/preview.
const editorMarkup = (html, index) =>
  html.replaceAll('data-edit="', `data-edit="elements[${index}].config.values.`);

// Compile one block of content — a section, or an inline `code` element.
//
// Errors are returned as content rather than thrown. A section's template is
// written by a person in a textarea and an unclosed tag is an ordinary typing
// mistake; putting the message where the block would have been says which of
// six blocks is wrong, which an exception taking down the preview does not.
export const render = (
  { html, style, script },
  { types, values },
  context,
  // Present only when the editor is composing the document, and carrying the
  // index of the element being rendered. See editorMarkup below.
  editor = null
) => {
  const locals = { content: {}, types, values, cxt: context };

  let body = "";
  if (html) {
    try {
      body = Engine.render(editor ? editorMarkup(html, editor.index) : html, locals);
    } catch (e) {
      // ESCAPED, and not for tidiness. V8 quotes the offending input back in a
      // parse error, and the engine names the offending construct, so the
      // message carries author-chosen text — an unescaped one is a path by
      // which a stored string reaches the page as markup through the ERROR
      // branch, on exactly the content this module renders carefully. The same
      // applies to the three siblings below and in ./elements and ./page.
      body = `<div style="color:#b00;font:12px monospace">Template error: ${Engine.escapeHtml(
        e.toString()
      )}</div>`;
    }
  }

  let styleRes = "";
  if (style) {
    try {
      styleRes = `<style>${Engine.render(style, locals)}</style>`;
    } catch (e) {
      // `-->` in the message would close this comment and put the rest of it in
      // the document as markup, so the escape matters more here than it looks.
      styleRes = `<!-- style error: ${Engine.escapeHtml(e.toString())} -->`;
    }
  }

  // Wrapped in try/catch in the OUTPUT, not here: a section's script runs in the
  // preview iframe, and one that throws on load would stop every later script on
  // the page from running at all.
  const scriptRes = script
    ? `<script>\ntry {\n${script}\n} catch (e) {\n  console.log(e.toString());\n}\n</script>`
    : "";

  return {
    // The body is markup, so a substituted value is TEXT — see the note on
    // `escape` above. The style and script streams stay raw: they are not HTML,
    // and `&amp;` inside a stylesheet or a script is corruption rather than
    // safety. What contains those two is the same thing that always did — the
    // sandboxed preview frame, and the separate hostname on a served page.
    body: interpolate(body, context, { escape: true }),
    style: interpolate(styleRes, context),
    script: interpolate(scriptRes, context),
  };
};
